import { useState } from "react";
import { Check, Loader2, Search, ShieldCheck, AlertTriangle, Send, RefreshCcw } from "lucide-react";

/* Interactive OFAC / watchlist screening demo for the core deck.
 * Fully client-side simulation — no real screening happens here. */

const SAMPLES = [
  { name: "Jordan Reyes", type: "Individual", hit: false },
  { name: "Halcyon Remit Ltd", type: "Business", hit: true },
];

const STEPS = [
  "OFAC SDN + Non-SDN consolidated…",
  "UN Security Council consolidated list…",
  "EU financial sanctions list…",
  "PEP database · state-owned enterprise flags…",
  "Adverse media scan · last 24 months…",
];

export function OfacDemo() {
  const [name, setName] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | running | done
  const [steps, setSteps] = useState([]);
  const [result, setResult] = useState(null);

  const run = (n) => {
    const query = (n || name).trim();
    if (!query) return;
    setName(query);
    setPhase("running");
    setSteps([]);
    setResult(null);
    STEPS.forEach((s, i) => setTimeout(() => setSteps((p) => [...p, s]), 200 + i * 340));
    setTimeout(() => {
      const sample = SAMPLES.find((s) => s.name.toLowerCase() === query.toLowerCase());
      setResult({ query, hit: sample ? sample.hit : false });
      setPhase("done");
    }, 200 + STEPS.length * 340 + 150);
  };

  const reset = () => { setPhase("idle"); setSteps([]); setResult(null); setName(""); };

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="ofac-demo">
      <div className="col-span-12 md:col-span-6">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1">
          / step 1 · screen a name (or pick a sample)
        </div>
        <div className="flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="Person or business name"
            data-testid="ofac-input"
            className="flex-1 border border-ink-900/12 rounded-md px-3 py-2 text-sm bg-white text-ink-900 focus:outline-none focus:border-sun-500"
          />
          <button
            onClick={() => run()}
            disabled={phase === "running" || !name.trim()}
            data-testid="ofac-run"
            className="inline-flex items-center gap-2 bg-ink-900 hover:bg-ink-800 disabled:opacity-40 text-cream rounded-md px-4 py-2 text-sm transition-colors"
          >
            {phase === "running" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            Screen
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {SAMPLES.map((s) => (
            <button
              key={s.name}
              onClick={() => run(s.name)}
              data-testid={`ofac-sample-${s.hit ? "hit" : "clean"}`}
              className="mono text-[10px] uppercase tracking-[0.18em] border border-ink-900/15 rounded px-2 py-1 bg-cream-100 hover:border-sun-500 hover:text-sun-700 transition-colors"
            >
              {s.name} · {s.type}
            </button>
          ))}
        </div>
        {steps.length > 0 && (
          <ul className="mt-3 space-y-1">
            {steps.map((s) => (
              <li key={s} className="flex items-center gap-2 text-xs text-ink-900/75 animate-fade-in">
                <Check className="h-3 w-3 text-emerald-700 flex-shrink-0" /> {s}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-[11px] text-ink-900/50 leading-snug">
          In production every screened profile is re-checked continuously — a new list match triggers an alert to your team the same day, not at the next annual review.
        </p>
      </div>

      <div className="col-span-12 md:col-span-6">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mb-1">/ step 2 · disposition</div>
        {phase !== "done" ? (
          <div className="border border-dashed border-ink-900/15 rounded-md p-4 bg-white/40 text-sm text-ink-900/55 text-center grid place-items-center" style={{ minHeight: 190 }}>
            {phase === "running" ? (
              <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Screening…</span>
            ) : (
              "Screen a name on the left to populate this panel."
            )}
          </div>
        ) : result.hit ? (
          <div className="border border-rose-500/30 rounded-md p-4 bg-rose-500/5 space-y-3 animate-fade-in" data-testid="ofac-result-hit">
            <div className="flex items-center justify-between gap-2">
              <div className="serif text-base text-ink-900">{result.query}</div>
              <div className="mono text-[10px] uppercase tracking-[0.22em] border border-rose-500/40 bg-rose-500/10 text-rose-700 rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                <AlertTriangle className="h-3 w-3" /> Potential match
              </div>
            </div>
            <div className="text-xs text-ink-900/70 leading-relaxed">
              92% name similarity to an OFAC SDN entry (counter-narcotics program) · registered agent overlaps with a sanctioned network. Funds hold recommended pending analyst review — do not tip off.
            </div>
            <button className="w-full mono text-[10px] uppercase tracking-[0.22em] text-cream bg-ink-900 hover:bg-ink-800 rounded px-3 py-2 inline-flex items-center justify-center gap-1.5" data-testid="ofac-escalate">
              <Send className="h-3 w-3" /> Escalate to CompliSun analyst
            </button>
            <button onClick={reset} className="w-full mono text-[10px] uppercase tracking-[0.22em] text-ink-900/60 hover:text-sun-700 inline-flex items-center justify-center gap-1.5" data-testid="ofac-reset">
              <RefreshCcw className="h-3 w-3" /> Screen another
            </button>
          </div>
        ) : (
          <div className="border border-emerald-500/25 rounded-md p-4 bg-emerald-500/5 space-y-3 animate-fade-in" data-testid="ofac-result-clean">
            <div className="flex items-center justify-between gap-2">
              <div className="serif text-base text-ink-900">{result.query}</div>
              <div className="mono text-[10px] uppercase tracking-[0.22em] border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" /> Clear
              </div>
            </div>
            <div className="text-xs text-ink-900/70 leading-relaxed">
              No matches across OFAC, UN, EU, or PEP lists. No material adverse media. Result logged with timestamp + list versions — enrolled in continuous re-screening.
            </div>
            <button onClick={reset} className="w-full mono text-[10px] uppercase tracking-[0.22em] text-ink-900/60 hover:text-sun-700 inline-flex items-center justify-center gap-1.5" data-testid="ofac-reset">
              <RefreshCcw className="h-3 w-3" /> Screen another
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
